/**
 * Client-side wishlist. State is stored in localStorage as an array of
 * product handles and is intentionally per-browser (no account/server sync).
 */
(function () {
  const STORAGE_KEY = 'flyball_wishlist';

  function readHandles() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeHandles(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      // localStorage unavailable (private mode, quota, etc) - fail silently.
    }
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', { detail: { handles: handles, count: handles.length } })
    );
  }

  const Wishlist = {
    get() {
      return readHandles();
    },
    has(handle) {
      return readHandles().includes(handle);
    },
    count() {
      return readHandles().length;
    },
    add(handle) {
      const handles = readHandles();
      if (!handles.includes(handle)) {
        handles.unshift(handle);
        writeHandles(handles);
      }
      return handles;
    },
    remove(handle) {
      const handles = readHandles().filter((item) => item !== handle);
      writeHandles(handles);
      return handles;
    },
    toggle(handle) {
      const isActive = !this.has(handle);
      if (isActive) {
        this.add(handle);
      } else {
        this.remove(handle);
      }
      return isActive;
    },
  };

  window.Wishlist = Wishlist;

  function updateCountBubbles() {
    const count = Wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach((bubble) => {
      const valueEl = bubble.querySelector('[data-wishlist-count-value]');
      if (valueEl) valueEl.textContent = count;
      bubble.classList.toggle('wishlist-count-bubble--empty', count === 0);
    });
  }

  document.addEventListener('wishlist:updated', updateCountBubbles);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateCountBubbles);
  } else {
    updateCountBubbles();
  }

  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class WishlistButton extends HTMLElement {
        connectedCallback() {
          this.handle = this.dataset.handle;
          this.button = this.querySelector('.wishlist-button__toggle');
          if (!this.handle || !this.button) return;

          this.onUpdate = this.render.bind(this, undefined);
          this.render();
          this.button.addEventListener('click', this.onClick.bind(this));
          document.addEventListener('wishlist:updated', this.onUpdate);
        }

        disconnectedCallback() {
          document.removeEventListener('wishlist:updated', this.onUpdate);
        }

        onClick(event) {
          event.preventDefault();
          const isActive = Wishlist.toggle(this.handle);
          this.render(isActive);
          this.button.classList.remove('wishlist-button__toggle--pop');
          // eslint-disable-next-line no-unused-expressions
          this.button.offsetWidth;
          this.button.classList.add('wishlist-button__toggle--pop');
        }

        render(activeOverride) {
          const active = typeof activeOverride === 'boolean' ? activeOverride : Wishlist.has(this.handle);
          this.button.classList.toggle('is-active', active);
          this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
          const strings = window.wishlistStrings || {};
          this.button.setAttribute('aria-label', active ? strings.remove || 'Remove from wishlist' : strings.add || 'Add to wishlist');
        }
      }
    );
  }

  function formatMoney(cents, format) {
    const formatString = format || '${{amount}}';
    if (isNaN(cents) || cents === null) return formatString.replace(/\{\{\s*\w+\s*\}\}/, '0.00');

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const remainder = parts[1] ? decimal + parts[1] : '';
      return dollars + remainder;
    }

    const placeholder = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = placeholder ? placeholder[1] : 'amount';
    let value;
    switch (key) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return formatString.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function productImageUrl(src, width) {
    if (!src) return src;
    return src.replace(/\.(jpe?g|png|gif|webp)(\?.*)?$/i, `_${width}x.$1$2`);
  }

  if (!customElements.get('wishlist-page')) {
    customElements.define(
      'wishlist-page',
      class WishlistPage extends HTMLElement {
        connectedCallback() {
          this.grid = this.querySelector('[data-wishlist-grid]');
          this.emptyState = this.querySelector('[data-wishlist-empty]');
          this.template = this.querySelector('template');
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

          this.onWishlistUpdate = this.load.bind(this);
          document.addEventListener('wishlist:updated', this.onWishlistUpdate);
          this.load();
        }

        disconnectedCallback() {
          document.removeEventListener('wishlist:updated', this.onWishlistUpdate);
        }

        async load() {
          // Wishlist mutations can fire in quick succession (e.g. removing two
          // items back to back), each starting its own async load(). Guard
          // against a slower, stale request clobbering a newer one's result.
          const requestId = (this._requestId = (this._requestId || 0) + 1);
          const handles = Wishlist.get();

          if (!handles.length) {
            if (requestId === this._requestId) this.showEmpty();
            return;
          }

          this.grid.hidden = false;
          this.grid.setAttribute('aria-busy', 'true');
          this.emptyState.hidden = true;

          const products = await Promise.all(
            handles.map((handle) =>
              fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
                .then((response) => (response.ok ? response.json() : null))
                .catch(() => null)
            )
          );

          if (requestId !== this._requestId) return;

          this.grid.innerHTML = '';
          products.forEach((product, index) => {
            if (!product) return;
            this.grid.appendChild(this.buildCard(product, handles[index]));
          });
          this.grid.setAttribute('aria-busy', 'false');

          if (!this.grid.children.length) {
            this.showEmpty();
          }
        }

        showEmpty() {
          this.grid.innerHTML = '';
          this.grid.hidden = true;
          this.emptyState.hidden = false;
        }

        buildCard(product, handle) {
          const fragment = this.template.content.cloneNode(true);
          const card = fragment.querySelector('[data-wishlist-card]');
          const mediaLink = fragment.querySelector('[data-wishlist-media-link]');
          const titleLink = fragment.querySelector('[data-wishlist-title-link]');
          const img = fragment.querySelector('[data-wishlist-image]');
          const priceEl = fragment.querySelector('[data-wishlist-price]');
          const compareEl = fragment.querySelector('[data-wishlist-compare-price]');
          const badgeEl = fragment.querySelector('[data-wishlist-badge]');
          const removeButton = fragment.querySelector('[data-wishlist-remove]');
          const addButton = fragment.querySelector('[data-wishlist-add]');
          const variantInput = fragment.querySelector('[data-wishlist-variant-id]');
          const form = fragment.querySelector('[data-wishlist-form]');

          const url = `/products/${handle}`;
          const image = product.featured_image || (product.images && product.images[0]);
          const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];

          card.dataset.handle = handle;
          mediaLink.href = url;
          titleLink.href = url;
          titleLink.textContent = product.title;

          if (image) {
            img.src = productImageUrl(image, 533);
            img.alt = product.title;
          } else {
            img.remove();
          }

          if (variant) {
            priceEl.textContent = formatMoney(variant.price, window.themeMoneyFormat);
            variantInput.value = variant.id;

            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              compareEl.textContent = formatMoney(variant.compare_at_price, window.themeMoneyFormat);
              compareEl.hidden = false;
              badgeEl.hidden = false;
            } else {
              compareEl.hidden = true;
              badgeEl.hidden = true;
            }

            if (!variant.available) {
              addButton.disabled = true;
              addButton.querySelector('span').textContent = window.wishlistStrings?.soldOut || 'Sold out';
            }
          } else {
            addButton.disabled = true;
          }

          removeButton.addEventListener('click', () => Wishlist.remove(handle));
          form.addEventListener('submit', this.onAddToCart.bind(this));

          return fragment;
        }

        onAddToCart(event) {
          event.preventDefault();
          const form = event.currentTarget;
          const button = form.querySelector('[data-wishlist-add]');
          const formData = new FormData(form);

          if (this.cart) {
            formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          button.classList.add('loading');
          button.setAttribute('aria-disabled', 'true');

          fetch(window.routes.cart_add_url, {
            method: 'POST',
            body: formData,
            headers: { Accept: 'application/json' },
          })
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                console.error(response);
                return;
              }
              if (this.cart) {
                this.cart.renderContents(response);
              } else {
                window.location = window.routes.cart_url;
              }
            })
            .catch((error) => console.error(error))
            .finally(() => {
              button.classList.remove('loading');
              button.removeAttribute('aria-disabled');
            });
        }
      }
    );
  }
})();
